import {
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Button,
  Icon,
  IconButton,
  InputRightElement,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { IconViewOff, IconView } from 'assets/icons';
import { FormikInput } from 'components/Formik';
import Loader from 'components/Loader';
import { useConnectUKG } from 'hooks/integrations/ukg/useConnectUKG';
import { useFetchUKGConfiguration } from 'hooks/integrations/ukg/useFetchUKGConfiguration';

import { connectFormValidationSchema, initialConnectFormData } from './schema';

interface IEvvConnectFormProps {
  onClose: () => void;
  type?: 'update' | 'create';
}

const ConnectForm: React.FC<IEvvConnectFormProps> = ({ onClose, type }) => {
  const { t } = useTranslation(['common', 'integrations']);

  const [formData, setFormData] = React.useState(initialConnectFormData);

  const { isFetching } = useFetchUKGConfiguration({
    enabled: type === 'update',
    onSuccess: (response: any) => {
      const responseData = response?.payload;

      const formData = {
        username: responseData?.username || '',
        password: responseData?.password || '',
        host: responseData?.host || '',
        client_id: responseData?.client_id || '',
        auth_guid: responseData?.auth_guid || '',
        show_password: false,
      };

      setFormData(formData);
    },
  });

  const { mutateAsync, isLoading } = useConnectUKG();

  const handleClickShowPassword = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    showPassword: boolean
  ) => {
    setFieldValue('show_password', !showPassword);
  };

  const handleFormSubmit = (values: typeof initialConnectFormData) => {
    mutateAsync(values).then(() => {
      onClose();
    });
  };

  return (
    <ModalContent>
      <ModalHeader>{type === 'create' ? t('integrations:ukg.connectToUKG') : 'Update UKG Configuration'}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        {isFetching ? (
          <Loader />
        ) : (
          <Formik
            enableReinitialize
            initialValues={formData}
            validationSchema={connectFormValidationSchema}
            onSubmit={handleFormSubmit}
          >
            {({ setFieldValue, values }) => (
              <Form noValidate>
                <FormikInput
                  type="text"
                  name="username"
                  label={t('common:username')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikInput
                  type={values.show_password ? 'text' : 'password'}
                  name="password"
                  label={t('common:password')}
                  formControlProps={{ isRequired: true }}
                  inputElement={
                    <InputRightElement width="4.5rem">
                      <IconButton
                        aria-label="toggle view password"
                        size="sm"
                        onClick={() => handleClickShowPassword(setFieldValue, values.show_password)}
                        icon={values.show_password ? <Icon as={IconViewOff} /> : <Icon as={IconView} />}
                      ></IconButton>
                    </InputRightElement>
                  }
                />
                <FormikInput type="text" name="host" label="Host" formControlProps={{ isRequired: true }} />
                <FormikInput type="text" name="client_id" label="Client ID" formControlProps={{ isRequired: true }} />
                <FormikInput type="text" name="auth_guid" label="Auth GUID" formControlProps={{ isRequired: true }} />

                <Box textAlign="right">
                  <Button bg="gray.600" _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.700' }} onClick={onClose}>
                    {t('common:close')}
                  </Button>
                  <Button type="submit" colorScheme="secondary" isLoading={isLoading} ml="5">
                    {type === 'create' ? t('integrations:connect') : t('common:update')}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </ModalBody>
    </ModalContent>
  );
};

export default ConnectForm;
